// Legacy export for backward compatibility
// This loads all teams at once - consider using teamLoader.js for better performance
import mlbTeams from './mlb';
import nflTeams from './nfl';
import cflTeams from './cfl';
import ncaaTeams from './ncaa';
import wnbaTeams from './wnba';
import nhlTeams from './nhl';
import nbaTeams from './nba';
import golf from './golf';
import ufc from './ufc';
import boxing from './boxing';
import tennis from './tennis';
import soccerTeams from './soccer';

const teamsDisplayNames = {
  ...mlbTeams,
  ...nflTeams,
  ...cflTeams,
  ...ncaaTeams,
  ...wnbaTeams,
  ...nhlTeams,
  ...nbaTeams,
  ...golf,
  ...ufc,
  ...boxing,
  ...tennis,
  ...soccerTeams,
};

export default teamsDisplayNames;

/**
 * Some leagues prefix participant longNames with a period token (e.g. F5 for
 * MLB First-5-Innings, 1H/2H for half-game lines, Q1–Q4 for quarter lines).
 * Strip that leading token, along with smart quotes / whitespace, so the
 * underlying team's displayName/logoUrl resolves through the regular
 * teamsDisplayNames keys (e.g. "F5 Pittsburgh Pirates" → "Pittsburgh Pirates").
 *
 * Returns the mapping entry (e.g. `{ displayName, logoUrl }`) or `undefined`.
 */
const PERIOD_PREFIX_RE = /^(?:F5|1H|2H|Q[1-4])\s+/;
const SMART_QUOTE_RE = /[\u2018\u2019\u02BC\uFF07]/g;
export function lookupTeamDisplay(longName) {
  if (!longName || typeof longName !== 'string') return undefined;
  const normalized = longName.replace(SMART_QUOTE_RE, "'").trim();
  const key = normalized.replace(PERIOD_PREFIX_RE, '');
  return teamsDisplayNames[key];
}

/**
 * Extract the leading period token (e.g. "F5 ", "1H ", "Q3 ") from a
 * participant longName. Returns the token including its trailing space so it
 * can be concatenated directly onto a display string, or an empty string if
 * the longName has no period prefix.
 */
export function getPeriodPrefix(longName) {
  if (!longName || typeof longName !== 'string') return '';
  const normalized = longName.replace(SMART_QUOTE_RE, "'").trim();
  const match = normalized.match(PERIOD_PREFIX_RE);
  return match ? match[0] : '';
}

/**
 * Prepend the period token from `longName` onto `base` (e.g. abbreviated team
 * name "PIT Pirates" → "F5 PIT Pirates" for an F5-MLB participant). No-op when
 * the longName has no period prefix, when base isn't a string, or when base
 * already starts with the same prefix (avoid double-prepending).
 */
export function applyPeriodPrefix(longName, base) {
  const prefix = getPeriodPrefix(longName);
  if (!prefix) return base;
  if (typeof base !== 'string' || base.length === 0) return base;
  if (base.startsWith(prefix)) return base;
  return prefix + base;
}

// Re-export the team loader utilities for better performance
export {
  getTeamsForSport,
  preloadTeams,
  clearTeamCache,
  getCachedTeams,
  isTeamDataCached,
} from '@/utils/teamLoader';
